import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { coursesAPI, playersAPI, roundsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { ROUTES } from '../utils/constants';

export default function NewRound() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [courses, setCourses] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  // Form state - Step 1: Event Details
  const [eventName, setEventName] = useState('');
  const [eventType, setEventType] = useState(''); // 'missing-links' or 'friday-bath-boys'

  // Form state - Step 2: Course
  const [selectedCourseId, setSelectedCourseId] = useState(null);

  // Form state - Step 3: Players (for each round in Missing Links)
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [groupAssignments, setGroupAssignments] = useState({});

  // For Missing Links - track players per round
  const [round1Players, setRound1Players] = useState([]); // Practice
  const [round2Players, setRound2Players] = useState([]); // Championship 1
  const [round3Players, setRound3Players] = useState([]); // Championship 2

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [coursesData, playersData] = await Promise.all([
        coursesAPI.getAll(),
        playersAPI.getAll()
      ]);
      setCourses(coursesData.courses || []);
      setPlayers(playersData.players || []);
    } catch (err) {
      setError('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePlayerToggle = (playerId) => {
    if (selectedPlayers.includes(playerId)) {
      // Remove player
      setSelectedPlayers(selectedPlayers.filter(id => id !== playerId));
      const newGroups = { ...groupAssignments };
      delete newGroups[playerId];
      setGroupAssignments(newGroups);
    } else {
      // Add player
      setSelectedPlayers([...selectedPlayers, playerId]);
      // Auto-assign to next available group (max 4 per group)
      const groupCounts = {};
      Object.values(groupAssignments).forEach(group => {
        groupCounts[group] = (groupCounts[group] || 0) + 1;
      });

      // Find first group with less than 4 players, or create new group
      let assignedGroup = 1;
      while (groupCounts[assignedGroup] >= 4) {
        assignedGroup++;
      }

      setGroupAssignments({
        ...groupAssignments,
        [playerId]: assignedGroup
      });
    }
  };

  const handleGroupChange = (playerId, newGroup) => {
    setGroupAssignments({
      ...groupAssignments,
      [playerId]: parseInt(newGroup)
    });
  };

  const handleCreateEvent = async () => {
    setError('');

    // Validation
    if (!eventName.trim()) {
      setError('Please enter an event name');
      return;
    }

    if (!eventType) {
      setError('Please select an event type');
      return;
    }

    if (!selectedCourseId) {
      setError('Please select a course');
      return;
    }

    if (eventType === 'missing-links') {
      // For Missing Links, validate we have players for at least Round 1
      if (round1Players.length < 2) {
        setError('Please select at least 2 players for Round 1 (Practice)');
        return;
      }
    } else {
      // For Friday Bath Boys, need regular player selection
      if (selectedPlayers.length < 2) {
        setError('Please select at least 2 players');
        return;
      }
    }

    try {
      setCreating(true);

      if (eventType === 'missing-links') {
        // Create 3 rounds for The Missing Links
        // Round 1: Practice
        if (round1Players.length >= 2) {
          const playersData1 = round1Players.map(playerId => ({
            playerId,
            groupNumber: groupAssignments[playerId] || 1
          }));

          await roundsAPI.create({
            courseId: selectedCourseId,
            players: playersData1,
            roundType: 'practice',
            eventName: `${eventName} - Round 1 (Practice)`
          });
        }

        // Round 2: Championship 1
        if (round2Players.length >= 2) {
          const playersData2 = round2Players.map(playerId => ({
            playerId,
            groupNumber: groupAssignments[playerId] || 1
          }));

          await roundsAPI.create({
            courseId: selectedCourseId,
            players: playersData2,
            roundType: 'championship',
            eventName: `${eventName} - Round 2 (Championship)`
          });
        }

        // Round 3: Championship 2
        if (round3Players.length >= 2) {
          const playersData3 = round3Players.map(playerId => ({
            playerId,
            groupNumber: groupAssignments[playerId] || 1
          }));

          await roundsAPI.create({
            courseId: selectedCourseId,
            players: playersData3,
            roundType: 'championship',
            eventName: `${eventName} - Round 3 (Championship)`
          });
        }

        alert('Event created successfully with 3 rounds!');
        navigate(ROUTES.HISTORY);
      } else {
        // Friday Bath Boys - single round
        const playersData = selectedPlayers.map(playerId => ({
          playerId,
          groupNumber: groupAssignments[playerId] || 1
        }));

        const createdRound = await roundsAPI.create({
          courseId: selectedCourseId,
          players: playersData,
          roundType: 'championship',
          eventName: eventName.trim()
        });

        navigate(ROUTES.ACTIVE_ROUND.replace(':id', createdRound.id));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create event: ' + err.message);
    } finally {
      setCreating(false);
    }
  };

  // Group players by their assigned group
  const getPlayersByGroup = (playerIds) => {
    const playersByGroup = {};
    playerIds.forEach(playerId => {
      const group = groupAssignments[playerId] || 1;
      if (!playersByGroup[group]) {
        playersByGroup[group] = [];
      }
      const player = players.find(p => p.id === playerId);
      if (player) {
        playersByGroup[group].push(player);
      }
    });
    return playersByGroup;
  };

  const playersByGroup = getPlayersByGroup(selectedPlayers);
  const groups = Object.keys(playersByGroup).sort((a, b) => a - b);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-4">New Event</h1>
        <Card>
          <p className="text-lg text-error">
            You must be logged in as admin to create a new event.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-4 pb-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Start New Event</h1>
      <p className="text-base text-gray-600 mb-6">
        Create an event, select a course, and assign players
      </p>

      {error && (
        <div className="bg-red-100 text-red-800 px-6 py-4 border border-red-300 mb-6">
          {error}
        </div>
      )}

      {/* Step 1: Event Details */}
      <Card title="1. Event Details" className="mb-6">
        <div className="space-y-4">
          {/* Event Name */}
          <div>
            <label className="block text-base font-semibold text-gray-700 mb-2">
              Event Name
            </label>
            <input
              type="text"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              placeholder="e.g., TML 2025"
              className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Event Type */}
          <div>
            <label className="block text-base font-semibold text-gray-700 mb-2">
              Event Type
            </label>
            <div className="space-y-3">
              <label className={`flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all ${
                eventType === 'missing-links'
                  ? 'border-primary bg-primary bg-opacity-5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  value="missing-links"
                  checked={eventType === 'missing-links'}
                  onChange={(e) => setEventType(e.target.value)}
                  className="w-5 h-5 text-primary focus:ring-primary mt-0.5"
                />
                <div className="ml-3">
                  <div className="font-semibold text-gray-900">The Missing Links</div>
                  <div className="text-sm text-gray-600 mt-1">
                    3-round annual event: 1 practice round + 2 championship rounds
                  </div>
                </div>
              </label>

              <label className={`flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all ${
                eventType === 'friday-bath-boys'
                  ? 'border-primary bg-primary bg-opacity-5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  value="friday-bath-boys"
                  checked={eventType === 'friday-bath-boys'}
                  onChange={(e) => setEventType(e.target.value)}
                  className="w-5 h-5 text-primary focus:ring-primary mt-0.5"
                />
                <div className="ml-3">
                  <div className="font-semibold text-gray-900">Friday Bath Boys</div>
                  <div className="text-sm text-gray-600 mt-1">
                    Single round championship event
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>
      </Card>

      {/* Step 2: Select Course */}
      {eventName && eventType && (
        <Card title="2. Select Course" className="mb-6">
          {courses.length === 0 ? (
            <p className="text-gray-600">No courses available. Please create a course first.</p>
          ) : (
            <div className="space-y-3">
              {courses.map((course) => (
                <button
                  key={course.id}
                  onClick={() => setSelectedCourseId(course.id)}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                    selectedCourseId === course.id
                      ? 'border-primary bg-primary bg-opacity-10'
                      : 'border-gray-200 hover:border-primary'
                  }`}
                >
                  <div className="font-bold text-lg text-gray-900">{course.name}</div>
                </button>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Step 3: Select Players */}
      {selectedCourseId && (
        <>
          {eventType === 'missing-links' ? (
            // Missing Links - 3 rounds with separate player selections
            <div className="space-y-6">
              {/* Round 1: Practice */}
              <Card title="3. Round 1 - Practice Round" className="mb-6">
                <p className="text-sm text-gray-600 mb-4">Select players for the practice round</p>
                {players.length === 0 ? (
                  <p className="text-gray-600">No players available.</p>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {players.map((player) => (
                      <label
                        key={player.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          round1Players.includes(player.id)
                            ? 'border-primary bg-primary bg-opacity-5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={round1Players.includes(player.id)}
                          onChange={() => {
                            if (round1Players.includes(player.id)) {
                              setRound1Players(round1Players.filter(id => id !== player.id));
                            } else {
                              setRound1Players([...round1Players, player.id]);
                            }
                          }}
                          className="w-5 h-5 text-primary focus:ring-primary"
                        />
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{player.name}</div>
                          <div className="text-sm text-gray-600">Handicap: {player.handicap}</div>
                        </div>
                        {round1Players.includes(player.id) && (
                          <select
                            value={groupAssignments[player.id] || 1}
                            onChange={(e) => handleGroupChange(player.id, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="px-3 py-2 border-2 border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-primary"
                          >
                            {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                              <option key={num} value={num}>
                                Group {num}
                              </option>
                            ))}
                          </select>
                        )}
                      </label>
                    ))}
                  </div>
                )}
              </Card>

              {/* Round 2: Championship 1 */}
              <Card title="4. Round 2 - Championship Round 1" className="mb-6">
                <p className="text-sm text-gray-600 mb-4">Select players for championship round 1</p>
                {players.length === 0 ? (
                  <p className="text-gray-600">No players available.</p>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {players.map((player) => (
                      <label
                        key={player.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          round2Players.includes(player.id)
                            ? 'border-primary bg-primary bg-opacity-5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={round2Players.includes(player.id)}
                          onChange={() => {
                            if (round2Players.includes(player.id)) {
                              setRound2Players(round2Players.filter(id => id !== player.id));
                            } else {
                              setRound2Players([...round2Players, player.id]);
                            }
                          }}
                          className="w-5 h-5 text-primary focus:ring-primary"
                        />
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{player.name}</div>
                          <div className="text-sm text-gray-600">Handicap: {player.handicap}</div>
                        </div>
                        {round2Players.includes(player.id) && (
                          <select
                            value={groupAssignments[player.id] || 1}
                            onChange={(e) => handleGroupChange(player.id, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="px-3 py-2 border-2 border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-primary"
                          >
                            {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                              <option key={num} value={num}>
                                Group {num}
                              </option>
                            ))}
                          </select>
                        )}
                      </label>
                    ))}
                  </div>
                )}
              </Card>

              {/* Round 3: Championship 2 */}
              <Card title="5. Round 3 - Championship Round 2" className="mb-6">
                <p className="text-sm text-gray-600 mb-4">Select players for championship round 2</p>
                {players.length === 0 ? (
                  <p className="text-gray-600">No players available.</p>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {players.map((player) => (
                      <label
                        key={player.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          round3Players.includes(player.id)
                            ? 'border-primary bg-primary bg-opacity-5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={round3Players.includes(player.id)}
                          onChange={() => {
                            if (round3Players.includes(player.id)) {
                              setRound3Players(round3Players.filter(id => id !== player.id));
                            } else {
                              setRound3Players([...round3Players, player.id]);
                            }
                          }}
                          className="w-5 h-5 text-primary focus:ring-primary"
                        />
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{player.name}</div>
                          <div className="text-sm text-gray-600">Handicap: {player.handicap}</div>
                        </div>
                        {round3Players.includes(player.id) && (
                          <select
                            value={groupAssignments[player.id] || 1}
                            onChange={(e) => handleGroupChange(player.id, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="px-3 py-2 border-2 border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-primary"
                          >
                            {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                              <option key={num} value={num}>
                                Group {num}
                              </option>
                            ))}
                          </select>
                        )}
                      </label>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          ) : (
            // Friday Bath Boys - Single round with player selection
            <Card title="3. Select Players" className="mb-6">
              {players.length === 0 ? (
                <p className="text-gray-600">No players available. Please add players first.</p>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {players.map((player) => (
                    <label
                      key={player.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedPlayers.includes(player.id)
                          ? 'border-primary bg-primary bg-opacity-5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedPlayers.includes(player.id)}
                        onChange={() => handlePlayerToggle(player.id)}
                        className="w-5 h-5 text-primary focus:ring-primary"
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">{player.name}</div>
                        <div className="text-sm text-gray-600">Handicap: {player.handicap}</div>
                      </div>
                      {selectedPlayers.includes(player.id) && (
                        <select
                          value={groupAssignments[player.id] || 1}
                          onChange={(e) => handleGroupChange(player.id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="px-3 py-2 border-2 border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                            <option key={num} value={num}>
                              Group {num}
                            </option>
                          ))}
                        </select>
                      )}
                    </label>
                  ))}
                </div>
              )}
            </Card>
          )}
        </>
      )}

      {/* Review and Create */}
      {selectedCourseId && (
        (eventType === 'missing-links' && round1Players.length > 0) ||
        (eventType === 'friday-bath-boys' && selectedPlayers.length > 0)
      ) && (
        <Card title="Review & Create" className="mt-6">
          <div className="space-y-4">
            <div>
              <p className="text-sm font-semibold text-gray-700">Event Name:</p>
              <p className="text-base text-gray-900">{eventName}</p>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-700">Event Type:</p>
              <p className="text-base text-gray-900">
                {eventType === 'missing-links' ? 'The Missing Links (3 rounds)' : 'Friday Bath Boys (1 round)'}
              </p>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-700">Course:</p>
              <p className="text-base text-gray-900">
                {courses.find(c => c.id === selectedCourseId)?.name}
              </p>
            </div>

            {eventType === 'missing-links' && (
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Players per Round:</p>
                <div className="space-y-1 text-sm">
                  <p>Round 1 (Practice): {round1Players.length} players</p>
                  <p>Round 2 (Championship): {round2Players.length} players</p>
                  <p>Round 3 (Championship): {round3Players.length} players</p>
                </div>
              </div>
            )}

            {eventType === 'friday-bath-boys' && selectedPlayers.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Groups:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {groups.map((groupNum) => (
                    <div key={groupNum} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <h4 className="font-semibold text-sm text-primary mb-2">
                        Group {groupNum} ({playersByGroup[groupNum].length} players)
                      </h4>
                      <div className="space-y-1">
                        {playersByGroup[groupNum].map((player) => (
                          <div key={player.id} className="text-sm text-gray-700">
                            {player.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end gap-4">
            <Button
              variant="secondary"
              onClick={() => {
                setEventName('');
                setEventType('');
                setSelectedCourseId(null);
                setSelectedPlayers([]);
                setGroupAssignments({});
                setRound1Players([]);
                setRound2Players([]);
                setRound3Players([]);
              }}
            >
              Clear All
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateEvent}
              disabled={creating}
            >
              {creating ? 'Creating Event...' : 'Create Event'}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
